import { z } from "zod";

import type { CommentPlacement } from "@common/types/issue";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { type CardComment, cardCommentsSchema, type CardParagraphAnchor, type RedlineTextProposal, redlineTextProposalSchema } from "@common/utils/redline/wire/redline-payload";

import { parseCommentToolResult, parseReplyToolResult } from "../comment-tool-result";

export const textRedlineOfToolContent = (content: string): RedlineTextProposal | null => {
  try {
    const parsed = redlineTextProposalSchema.safeParse((JSON.parse(content) as { redline?: unknown }).redline);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const tableOfContentsRedirectSchema = z.object({
  entry_paragraph_id: z.string(),
  heading_paragraph_id: z.string(),
  heading_text: z.string(),
});

export type TableOfContentsRedirect = z.infer<typeof tableOfContentsRedirectSchema>;

const operationLeftOutNoteSchema = z.object({
  operation_index: z.number(),
  reason: z.string(),
  message: z.string(),
});

export type OperationLeftOutNote = z.infer<typeof operationLeftOutNoteSchema>;

export interface EditCardContent {
  kind: "redline" | "table";
  textRedline: RedlineTextProposal | null;
  markup: string | null;
  bookmarks: string[];
  afterBookmarks?: string[];
  tableOfContentsRedirects: TableOfContentsRedirect[];
  comments: CardComment[];
  operationsLeftOut?: OperationLeftOutNote[];
}

export interface NewParagraphCardContent {
  kind: "paragraph";
  textRedline: RedlineTextProposal | null;
  markup: string | null;
  precedingParagraphBookmark: string;
  precedingParagraphText: string | null;
  newParagraphTexts: string[];
  bookmarks?: string[];
  afterBookmarks?: string[];
  comments: CardComment[];
}

export interface CommentCardContent {
  kind: "comment";
  text: string;
  bookmarks: string[];
  anchorText: string | null;
  placement: CommentPlacement | null;
  paragraphText: string | null;
  paragraphAnchor: CardParagraphAnchor | null;
}

export interface ReplyCardContent {
  kind: "reply";
  text: string;
  targetCommentId: string;
  targetCommentText: string | null;
}

export type ProposalCardContent = EditCardContent | NewParagraphCardContent | CommentCardContent | ReplyCardContent;

const editToolResultSchema = z.object({
  ooxml: z.union([z.string(), z.array(z.string())]).optional(),
  redline: z.unknown().optional(),
  bookmarks: z.array(z.string()).optional(),
  after_bookmarks: z.array(z.string()).optional(),
  original_section_ooxml: z.string().optional(),
  table_of_contents_redirects: z.array(tableOfContentsRedirectSchema).optional(),
  comments: cardCommentsSchema,
  operations_left_out: z.array(operationLeftOutNoteSchema).optional(),
});

type EditToolResult = z.infer<typeof editToolResultSchema>;

export const cardCommentsOfToolContent = (content: string): CardComment[] => {
  const toolResult = editToolResultOf(content);
  return toolResult === null ? [] : toolResult.comments;
};

const EDIT_TOOL_NAMES = new Set(["redline_selection", "redline_section", "edit_table", "change_throughout"]);

function editToolResultOf(messageContent: string): EditToolResult | null {
  try {
    const parsed = editToolResultSchema.safeParse(JSON.parse(messageContent));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function firstMarkupOf(toolResult: EditToolResult): string | null {
  const markups = (Array.isArray(toolResult.ooxml) ? toolResult.ooxml : [toolResult.ooxml]).filter((markup): markup is string => typeof markup === "string" && markup.length > 0);
  return markups[0] ?? null;
}

function textRedlineOf(toolResult: EditToolResult): RedlineTextProposal | null {
  const parsed = redlineTextProposalSchema.safeParse(toolResult.redline);
  return parsed.success ? parsed.data : null;
}

function acceptedTextsOf(markup: string | null | undefined): string[] {
  if (markup === null || markup === undefined || markup.length === 0) return [];
  try {
    return new OoxmlRange(markup).allChangesAcceptedTexts().filter((text) => text.trim().length > 0);
  } catch {
    return [];
  }
}

function newParagraphContentOf(toolResult: EditToolResult): NewParagraphCardContent | null {
  const precedingParagraphBookmark = toolResult.after_bookmarks?.[0] ?? toolResult.bookmarks?.[0];
  if (precedingParagraphBookmark === undefined) return null;
  const markup = firstMarkupOf(toolResult);
  const textRedline = textRedlineOf(toolResult);
  const precedingParagraphTexts = acceptedTextsOf(toolResult.original_section_ooxml);
  const newParagraphTexts = markup === null ? (textRedline?.insertions ?? []).map((insertion) => insertion.textProposed) : acceptedTextsOf(markup);
  return {
    kind: "paragraph",
    textRedline,
    markup,
    precedingParagraphBookmark,
    precedingParagraphText: precedingParagraphTexts[precedingParagraphTexts.length - 1] ?? null,
    newParagraphTexts,
    bookmarks: toolResult.bookmarks ?? toolResult.after_bookmarks ?? [],
    afterBookmarks: toolResult.after_bookmarks ?? [],
    comments: toolResult.comments,
  };
}

export function proposalCardContentOf(toolName: string | undefined, messageContent: string, messageBookmarks: string[] | undefined): ProposalCardContent | null {
  if (toolName === "add_comment") {
    const comment = parseCommentToolResult(messageContent);
    if (comment.kind !== "comment") return null;
    return {
      kind: "comment",
      text: comment.text,
      bookmarks: comment.bookmarks ?? messageBookmarks ?? [],
      anchorText: comment.anchorText,
      placement: comment.placement,
      paragraphText: comment.paragraphText,
      paragraphAnchor: comment.paragraphAnchor,
    };
  }
  if (toolName === "reply_to_comment") {
    const reply = parseReplyToolResult(messageContent);
    return reply.kind === "reply" ? { kind: "reply", text: reply.text, targetCommentId: reply.targetCommentId, targetCommentText: reply.targetCommentText } : null;
  }
  const toolResult = editToolResultOf(messageContent);
  if (toolResult === null) return null;
  if (toolName === "add_paragraph") return newParagraphContentOf(toolResult);
  if (toolName === undefined || !EDIT_TOOL_NAMES.has(toolName)) return null;
  const textRedline = textRedlineOf(toolResult);
  return {
    kind: toolName === "edit_table" || textRedline?.cardKind === "table" ? "table" : "redline",
    textRedline,
    markup: firstMarkupOf(toolResult),
    bookmarks: toolResult.bookmarks ?? toolResult.after_bookmarks ?? [],
    afterBookmarks: toolResult.after_bookmarks ?? [],
    tableOfContentsRedirects: toolResult.table_of_contents_redirects ?? [],
    comments: toolResult.comments,
    operationsLeftOut: toolResult.operations_left_out ?? [],
  };
}
